import Component from "@glimmer/component";
import { cached, tracked } from "@glimmer/tracking";
import { hash } from "@ember/helper";
import { action } from "@ember/object";
import { service } from "@ember/service";
import Form from "discourse/components/form";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import {
  AUTOMATICALLY_TRANSLATE_COOKIE,
  AUTOMATICALLY_TRANSLATE_COOKIE_EXPIRY,
  automaticallyTranslate,
  languageSwitcherEnabled,
  LOCALE_COOKIE,
  LOCALE_COOKIE_EXPIRY,
  normalizeUnderstoodLanguages,
} from "discourse/lib/content-localization";
import cookie from "discourse/lib/cookie";
import getURL from "discourse/lib/get-url";
import MultiSelect from "discourse/select-kit/components/multi-select";
import DButton from "discourse/ui-kit/d-button";
import DModal from "discourse/ui-kit/d-modal";
import DModalCancel from "discourse/ui-kit/d-modal-cancel";
import I18n, { i18n } from "discourse-i18n";

export default class ContentLanguagePreferencesModal extends Component {
  @service currentUser;
  @service siteSettings;
  @service languageNameLookup;

  @tracked formApi;
  @tracked saving = false;

  @cached
  get data() {
    const interfaceLanguage = this.currentUser?.effective_locale ?? I18n.locale;

    return {
      interfaceLanguage,
      understoodLanguages: normalizeUnderstoodLanguages(
        this.currentUser?.user_option?.understood_languages
      ),
      automaticallyTranslate: automaticallyTranslate(this.currentUser),
    };
  }

  get allLanguageOptions() {
    return this.#toOptions(this.siteSettings.available_locales);
  }

  get interfaceLanguageOptions() {
    // Anonymous visitors change locale through the `locale` cookie. When the language switcher is
    // what makes that cookie honoured, the server only accepts the site's configured locales, so
    // offering the rest would silently discard the choice.
    if (
      !this.currentUser &&
      !this.siteSettings.set_locale_from_cookie &&
      languageSwitcherEnabled(this.siteSettings)
    ) {
      return this.#toOptions(
        this.siteSettings.available_content_localization_locales
      );
    }

    return this.allLanguageOptions;
  }

  get canChangeInterfaceLanguage() {
    if (!this.siteSettings.allow_user_locale) {
      return false;
    }

    return (
      !!this.currentUser ||
      this.siteSettings.set_locale_from_cookie ||
      languageSwitcherEnabled(this.siteSettings)
    );
  }

  get interfaceLanguageReadOnly() {
    return !this.canChangeInterfaceLanguage;
  }

  get loginPath() {
    return getURL("/login");
  }

  @action
  registerFormApi(formApi) {
    this.formApi = formApi;
  }

  @action
  submit() {
    this.formApi.submit();
  }

  @action
  setInterfaceLanguage(value, { set }) {
    return set("interfaceLanguage", value);
  }

  @action
  setUnderstoodLanguages(value, { set }) {
    return set("understoodLanguages", normalizeUnderstoodLanguages(value));
  }

  @action
  async save(data) {
    this.saving = true;

    try {
      if (this.currentUser) {
        const understoodLanguages = normalizeUnderstoodLanguages(
          data.understoodLanguages
        );

        const preferences = {
          automatically_translate: data.automaticallyTranslate,
          understood_languages: understoodLanguages,
        };
        if (this.canChangeInterfaceLanguage) {
          preferences.locale = data.interfaceLanguage;
        }

        await ajax(`/u/${this.currentUser.username}.json`, {
          type: "PUT",
          contentType: "application/json",
          data: JSON.stringify(preferences),
        });
        if (this.canChangeInterfaceLanguage) {
          this.currentUser.set("locale", data.interfaceLanguage);
          this.currentUser.set("effective_locale", data.interfaceLanguage);
        }
        this.currentUser.set(
          "user_option.automatically_translate",
          data.automaticallyTranslate
        );
        this.currentUser.set(
          "user_option.understood_languages",
          understoodLanguages
        );
      } else {
        if (this.canChangeInterfaceLanguage) {
          cookie(LOCALE_COOKIE, data.interfaceLanguage, {
            path: getURL("/"),
            expires: LOCALE_COOKIE_EXPIRY,
          });
        }
        cookie(AUTOMATICALLY_TRANSLATE_COOKIE, data.automaticallyTranslate, {
          path: "/",
          expires: AUTOMATICALLY_TRANSLATE_COOKIE_EXPIRY,
        });
      }

      this.args.closeModal();
      window.location.reload();
    } catch (error) {
      popupAjaxError(error);
      this.saving = false;
    }
  }

  #toOptions(locales) {
    return (locales ?? []).map(({ value }) => ({
      name: this.languageNameLookup.getLanguageName(value),
      value,
      id: value,
    }));
  }

  <template>
    <DModal
      class="content-language-preferences-modal"
      @closeModal={{@closeModal}}
      @inline={{@inline}}
      @title={{i18n "content_localization.preferences.title"}}
    >
      <:body>
        <Form
          @data={{this.data}}
          @onRegisterApi={{this.registerFormApi}}
          @onSubmit={{this.save}}
          as |form|
        >
          <form.Field
            @disabled={{this.interfaceLanguageReadOnly}}
            @format="full"
            @name="interfaceLanguage"
            @onSet={{this.setInterfaceLanguage}}
            @title={{i18n "user.locale.title"}}
            @type="select"
            @validation="required"
            as |field|
          >
            <field.Control as |select|>
              {{#each this.interfaceLanguageOptions as |language|}}
                <select.Option @value={{language.value}}>
                  {{language.name}}
                </select.Option>
              {{/each}}
            </field.Control>
          </form.Field>

          {{#if this.currentUser}}
            <form.Field
              @description={{i18n
                "user.content_languages.understood_description"
              }}
              @format="full"
              @name="understoodLanguages"
              @onSet={{this.setUnderstoodLanguages}}
              @showOptional={{false}}
              @title={{i18n "user.content_languages.understood"}}
              @type="custom"
              as |field|
            >
              <field.Control>
                <MultiSelect
                  @content={{this.allLanguageOptions}}
                  @langProperty="value"
                  @onChange={{field.set}}
                  @options={{hash filterable=true}}
                  @value={{field.value}}
                  @valueProperty="value"
                />
              </field.Control>
            </form.Field>
          {{else}}
            <div class="content-language-preferences-modal__understood">
              <span>{{i18n "user.content_languages.understood"}}</span>
              <a href={{this.loginPath}}>{{i18n
                  "content_localization.preferences.login_to_set_languages"
                }}</a>
            </div>
          {{/if}}

          <form.Field
            @format="full"
            @name="automaticallyTranslate"
            @title={{i18n "user.automatically_translate"}}
            @type="checkbox"
            as |field|
          >
            <field.Control data-test-automatically-translate />
          </form.Field>
        </Form>
      </:body>

      <:footer>
        <DButton
          class="btn-primary"
          @action={{this.submit}}
          @disabled={{this.saving}}
          @label="save"
        />
        <DModalCancel @close={{@closeModal}} />
      </:footer>
    </DModal>
  </template>
}
